import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { ChangeCallback, TouchedCallback } from '@fmnts/common/forms';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  AddressComponentMappingResult,
  GoogleMapsService,
} from '@formunauts/shared/vendors/google-maps/data-access';
import { take } from 'rxjs';
type LonLat = [lon: number, lat: number];
export interface AddressComponentMapping {
  [fieldKey: string]: string[];
}

export interface PlaceChangedEvent {
  /**
   * The original event information.
   * This is provider specific.
   */
  original: any;
  /**
   * Mapped address components. This will be only available,
   * if a mappping was provided.
   */
  mapped?: AddressComponentMappingResult;
  /**
   * Coordinaates of the selected place
   */
  coordinates?: LonLat;
}

/**
 * Adds a address lookup to an `HTMLInputElement`
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'input[appAddressLookup]',
  standalone: true,
})
export class AddressLookupDirective implements ControlValueAccessor, OnInit {
  @Input({ required: true }) mapping: AddressComponentMapping = {};
  @Output() placeChanged = new EventEmitter<PlaceChangedEvent>();

  private _value = '';
  private _onChange: ChangeCallback<string> = () => {};
  public _onTouched: TouchedCallback = () => {};

  constructor(
    private element: ElementRef<HTMLInputElement>,
    private googleMapsService: GoogleMapsService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    switch (this.provider) {
      case 'google':
        this.initializeGoogleSearch();
    }
  }

  get provider(): 'google' {
    // We might support different providers in the future
    return 'google';
  }

  get value(): string {
    return this._value;
  }

  set value(val: string) {
    this._value = val;

    this._onChange(val);
    this._onTouched();
  }

  writeValue(value: string): void {
    this._value = value;
  }

  registerOnChange(fn: ChangeCallback): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: TouchedCallback): void {
    this._onTouched = fn;
  }

  private initializeGoogleSearch() {
    // Wait for the API to stabilize
    this.googleMapsService.stable$.pipe(take(1)).subscribe((api) => {
      // If the google library isn't there for some reason, don't use autocomplete
      if (!api || google === undefined) {
        return;
      }

      // Set up the autocomplete widget
      const inputElement = this.element.nativeElement;
      const resetPlaceholder = !inputElement.placeholder;
      const autocomplete = new google.maps.places.Autocomplete(inputElement, {
        fields: ['formatted_address', 'address_components', 'geometry'],
        types: ['address'],
      });

      if (resetPlaceholder) {
        // We want to prevent the default placeholder added by the widget, if non is available
        inputElement.placeholder = '';
      }

      // Set up the listener for place changes
      autocomplete.addListener('place_changed', () =>
        // Get back in the zone
        this.ngZone.run(() => {
          // Prevent focusing elements underneath the clicked list entry
          (document.activeElement as HTMLElement).blur();

          const place = autocomplete.getPlace();
          const { mapping } = this;

          // Get mapped, if available
          const mapped = mapping
            ? this.googleMapsService.mapAddressComponents(place, mapping)
            : undefined;
          // Get coordinates, if available
          const coordinates: LonLat | undefined = place.geometry?.location
            ? [place.geometry.location.lng(), place.geometry.location.lat()]
            : undefined;

          this.placeChanged.emit({
            original: place,
            mapped,
            coordinates,
          });

          this.value = place.formatted_address || '';
        }),
      );

      // Set up a listener to prevent form submission when a place is selected via ENTER
      inputElement.addEventListener('keydown', (e) => {
        if (
          'Enter' === e.key &&
          this.googleMapsService.findOpenAutocompleteBox()
        ) {
          e.preventDefault();
        }
      });
    });
  }
}
